/**
 * Self Connection Exercise Modal - Guided exercise experience
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { 
  Clock, CheckCircle2, ArrowRight, ArrowLeft,
  Sparkles, Play
} from 'lucide-react';

interface Exercise {
  id: string;
  title: string;
  description: string;
  duration: string;
  category: string;
  difficulty: string;
  icon: React.ElementType;
  benefits: string[];
}

interface SelfConnectionExerciseModalProps {
  exercise: Exercise;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (responses: Record<number, string>) => void;
  isCompleted?: boolean;
}

const EXERCISE_CONTENT: Record<string, { intro: string; steps: { title: string; instruction: string; prompt?: string }[] }> = {
  'body-scan-awareness': {
    intro: 'This exercise will guide you through a gentle scan of your body, helping you notice physical sensations and what they might be telling you about your emotional state.',
    steps: [
      { title: 'Settle In', instruction: 'Find a comfortable position. You can sit or lie down. Close your eyes if that feels comfortable, or soften your gaze.' },
      { title: 'Breath Awareness', instruction: 'Take three deep breaths. Notice the rise and fall of your chest and belly. No need to change anything - just observe.' },
      { title: 'Scan Your Head', instruction: 'Bring attention to your face. Notice any tension in your forehead, jaw, or around your eyes. What sensations are present?', prompt: 'What do you notice in your head and face?' },
      { title: 'Scan Your Shoulders', instruction: 'Move your attention down to your shoulders, arms, and chest. Notice any tightness, warmth, or other sensations.', prompt: 'What sensations are in your upper body?' },
      { title: 'Scan Your Core', instruction: 'Notice your belly and lower back. This area often holds emotions. What do you feel here?', prompt: 'What do you notice in your core?' },
      { title: 'Scan Your Lower Body', instruction: 'Finally, notice your legs and feet. Feel your connection to the ground or surface beneath you.', prompt: 'What sensations are in your legs and feet?' },
      { title: 'Integration', instruction: 'Take a full breath in and notice your body as a whole. What is your body telling you right now?', prompt: 'What message is your body sharing with you today?' },
    ],
  },
  'emotional-check-in': {
    intro: 'This exercise helps you identify and name your current emotional state, creating space for self-understanding and acceptance.',
    steps: [
      { title: 'Pause', instruction: 'Take a moment to pause from whatever you were doing. Give yourself permission to check in with yourself.' },
      { title: 'Breathe', instruction: 'Take three slow breaths to create a moment of stillness.' },
      { title: 'Notice', instruction: 'Ask yourself: What am I feeling right now? Do not judge or analyse - just notice.', prompt: 'What emotions are present for you right now?' },
      { title: 'Name', instruction: 'Try to name the emotion(s). Be specific: Instead of bad, try disappointed or anxious or overwhelmed.', prompt: 'What specific emotion words describe what you feel?' },
      { title: 'Accept', instruction: 'Whatever you are feeling is valid. Say to yourself: It makes sense that I feel this way.', prompt: 'Why might it make sense that you feel this way?' },
    ],
  },
  'self-compassion-break': {
    intro: 'The Self-Compassion Break is a simple three-step practice you can use anytime you are struggling or experiencing difficulty.',
    steps: [
      { title: 'Mindfulness', instruction: 'Acknowledge that this is a moment of suffering. Say to yourself: This is hard or This is a moment of difficulty.', prompt: 'How would you acknowledge what you are going through?' },
      { title: 'Common Humanity', instruction: 'Remind yourself that suffering is part of being human. Others have felt this way too. You are not alone.', prompt: 'How can you remind yourself that others share in struggles like yours?' },
      { title: 'Self-Kindness', instruction: 'Place your hand over your heart. Ask yourself: What do I need right now? Offer yourself words of kindness.', prompt: 'What kind words would you offer yourself right now?' },
    ],
  },
  'inner-child-dialogue': {
    intro: 'This visualisation helps you connect with and nurture your inner child - the younger part of you that may still carry old wounds or unmet needs.',
    steps: [
      { title: 'Create Safety', instruction: 'Close your eyes and take a few deep breaths. Imagine a safe, warm space - perhaps a cozy room or a peaceful garden.' },
      { title: 'Meet Your Younger Self', instruction: 'In this safe space, imagine your younger self appearing. What age are they? What do they look like?', prompt: 'Describe your younger self as they appear to you.' },
      { title: 'Observe', instruction: 'Notice how your younger self seems. Are they happy, scared, lonely, or something else?', prompt: 'What do you notice about your younger self?' },
      { title: 'Connect', instruction: 'Approach your younger self with warmth. What does this younger part of you need to hear?', prompt: 'What words of comfort or reassurance would you offer?' },
      { title: 'Offer Care', instruction: 'Imagine giving your younger self what they needed back then - perhaps a hug, safety, or just your presence.', prompt: 'What are you offering your younger self?' },
      { title: 'Close', instruction: 'Before leaving, let your younger self know you will always be there for them. Gently return to the present moment.' },
    ],
  },
  'values-clarification': {
    intro: 'This exercise helps you identify what truly matters to you, giving you a compass for making decisions aligned with your authentic self.',
    steps: [
      { title: 'Reflect on Peak Moments', instruction: 'Think of a time when you felt truly alive, fulfilled, or proud. What was happening?', prompt: 'Describe a peak moment in your life. What made it meaningful?' },
      { title: 'Identify Underlying Values', instruction: 'What values were being honoured in that moment? (e.g., creativity, connection, freedom, growth, security)', prompt: 'What values were present in that experience?' },
      { title: 'Consider Challenges', instruction: 'Think of a time you felt angry or frustrated. Often anger signals a value being violated.', prompt: 'What value might have been compromised when you felt upset?' },
      { title: 'Name Your Core Values', instruction: 'Based on your reflections, what are your 3-5 most important values?', prompt: 'List your core values (3-5):' },
      { title: 'Assess Alignment', instruction: 'How aligned is your current life with these values? Where might you make adjustments?', prompt: 'How well are you living in alignment with these values?' },
    ],
  },
  'loving-kindness-self': {
    intro: 'Loving-kindness meditation directs phrases of goodwill toward yourself, cultivating self-compassion and inner warmth.',
    steps: [
      { title: 'Settle', instruction: 'Find a comfortable position. Place your hand on your heart if that feels supportive. Close your eyes.' },
      { title: 'Recall Goodness', instruction: 'Bring to mind something you appreciate about yourself, or a moment when someone showed you kindness.' },
      { title: 'Offer Phrases', instruction: 'Silently repeat these phrases to yourself: May I be happy. May I be healthy. May I be safe. May I live with ease.' },
      { title: 'Feel the Words', instruction: 'Continue repeating the phrases, allowing any warmth or softening that arises. If your mind wanders, gently return to the phrases.' },
      { title: 'Personalise', instruction: 'If helpful, modify the phrases to what you most need to hear right now.', prompt: 'What personalised phrases of loving-kindness would you offer yourself?' },
    ],
  },
  'boundary-awareness': {
    intro: 'This exercise helps you understand your current boundaries - where they may be too rigid, too loose, or just right.',
    steps: [
      { title: 'Define Boundaries', instruction: 'Boundaries are the limits we set that protect our time, energy, and wellbeing. They are essential for healthy relationships.' },
      { title: 'Physical Boundaries', instruction: 'Consider your physical space and touch preferences. Where do you feel comfortable? Where do you need more boundary?', prompt: 'How are your physical boundaries? Where might they need adjustment?' },
      { title: 'Emotional Boundaries', instruction: 'Think about how much you take on others emotions. Do you often feel responsible for how others feel?', prompt: 'How are your emotional boundaries? Do you take on too much?' },
      { title: 'Time Boundaries', instruction: 'Consider how you spend your time. Do you often overcommit? Say yes when you mean no?', prompt: 'How are your time boundaries? Where do you overextend?' },
      { title: 'Energy Boundaries', instruction: 'What drains your energy? What or who energizes you?', prompt: 'What drains your energy? What replenishes it?' },
      { title: 'Action Step', instruction: 'Choose one boundary you would like to strengthen this week.', prompt: 'What boundary will you focus on strengthening?' },
    ],
  },
  'saying-no-practice': {
    intro: 'Learning to say no is an act of self-respect. This exercise helps you practice declining requests that do not serve you.',
    steps: [
      { title: 'Understand Your Patterns', instruction: 'Think about times you said yes when you wanted to say no. What drove that?', prompt: 'What makes it hard for you to say no?' },
      { title: 'Know Your Rights', instruction: 'You have the right to: protect your time and energy, prioritise your wellbeing, change your mind, say no without guilt.' },
      { title: 'Practice Phrases', instruction: 'Try these: Thank you for thinking of me, but I cannot. I am not available for that. Let me think about it and get back to you.', prompt: 'Which phrase feels most comfortable for you? Create your own version.' },
      { title: 'Visualise', instruction: 'Imagine a situation where you need to say no. Visualise yourself calmly declining. Notice how it feels.', prompt: 'Describe a situation where you want to practice saying no.' },
      { title: 'Commit', instruction: 'Choose one situation this week where you will practice saying no (or negotiating).', prompt: 'What specific situation will you practice with?' },
    ],
  },
  'inner-critic-dialogue': {
    intro: 'We all have an inner critic - that voice that judges, criticizes, and often is not very kind. This exercise helps you understand and respond to it.',
    steps: [
      { title: 'Identify the Voice', instruction: 'Notice your inner critic. What does it say? What tone does it use? When is it loudest?', prompt: 'What does your inner critic typically say to you?' },
      { title: 'Recognise the Origin', instruction: 'This voice often developed to protect you or from absorbed criticism. Where might it have come from?', prompt: 'Where do you think this critical voice originated?' },
      { title: 'Name It', instruction: 'Some people find it helpful to give the inner critic a name or image. This creates some distance.', prompt: 'If you gave your inner critic a name or character, what would it be?' },
      { title: 'Respond with Wisdom', instruction: 'How would a wise, compassionate mentor respond to your inner critic message?', prompt: 'What would a kind, wise voice say in response to the critic?' },
      { title: 'Reframe', instruction: 'Transform the critic message into constructive guidance.', prompt: 'How can you transform the criticism into supportive guidance?' },
    ],
  },
  'future-self-letter': {
    intro: 'Imagine receiving a letter from your future self - someone who has grown through current challenges and achieved greater wisdom.',
    steps: [
      { title: 'Ground Yourself', instruction: 'Close your eyes and take a few deep breaths. Let yourself settle into a calm, receptive state.' },
      { title: 'Imagine Your Future Self', instruction: 'Picture yourself 5 or 10 years from now. This future version has grown, learned, and flourished. What do they look like? How do they carry themselves?', prompt: 'Describe your future self.' },
      { title: 'Receive the Letter', instruction: 'Your future self has written you a letter. They want to share wisdom, encouragement, and reassurance.', prompt: 'What does your future self want you to know about your current struggles?' },
      { title: 'Advice for Now', instruction: 'What advice does your future self offer for right now?', prompt: 'What specific advice does your future self give you?' },
      { title: 'Words of Encouragement', instruction: 'What words of love and encouragement does your future self share?', prompt: 'Write the closing of the letter - words of love from your future self.' },
    ],
  },
  'needs-inventory': {
    intro: 'We all have fundamental needs - physical, emotional, mental, and relational. This exercise helps you assess how well your needs are being met.',
    steps: [
      { title: 'Physical Needs', instruction: 'Consider: rest, nutrition, movement, touch, shelter, health. How well are these being met?', prompt: 'Rate your physical needs (1-10). Which need attention?' },
      { title: 'Emotional Needs', instruction: 'Consider: safety, acceptance, appreciation, support, belonging. How well are these being met?', prompt: 'Rate your emotional needs (1-10). Which need attention?' },
      { title: 'Mental Needs', instruction: 'Consider: stimulation, creativity, learning, accomplishment, purpose. How well are these being met?', prompt: 'Rate your mental needs (1-10). Which need attention?' },
      { title: 'Relational Needs', instruction: 'Consider: connection, intimacy, community, solitude, understanding. How well are these being met?', prompt: 'Rate your relational needs (1-10). Which need attention?' },
      { title: 'Prioritise', instruction: 'Which 1-2 needs feel most important to address right now? What small step could you take?', prompt: 'What need will you prioritise? What one small step will you take?' },
    ],
  },
  'authentic-self-exploration': {
    intro: 'Beneath our social roles and adaptations lies our authentic self. This exercise helps you connect with who you truly are.',
    steps: [
      { title: 'List Your Roles', instruction: 'Write down the roles you play: parent, employee, friend, partner, etc.', prompt: 'What roles do you play in your life?' },
      { title: 'Notice Adaptations', instruction: 'For each role, how do you adapt or change yourself? What do you hide or exaggerate?', prompt: 'How do you adapt yourself in these different roles?' },
      { title: 'Childhood Self', instruction: 'Before you learned to adapt, what were you like? What did you love? What came naturally?', prompt: 'What were you like as a child before you learned to adapt?' },
      { title: 'Core Qualities', instruction: 'Strip away the roles. What qualities feel essentially you - regardless of context?', prompt: 'What qualities feel most essentially YOU?' },
      { title: 'Permission', instruction: 'What parts of yourself have you hidden that want expression? What would it mean to let them out?', prompt: 'What parts of your authentic self want more expression?' },
      { title: 'Integration', instruction: 'How can you bring more of your authentic self into your daily life?', prompt: 'One way I will express my authentic self this week:' },
    ],
  },
};

export function SelfConnectionExerciseModal({
  exercise,
  open,
  onOpenChange,
  onComplete,
  isCompleted,
}: SelfConnectionExerciseModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<number, string>>({});
  const [isStarted, setIsStarted] = useState(false);

  const content = EXERCISE_CONTENT[exercise.id] || {
    intro: exercise.description,
    steps: [{ title: 'Reflect', instruction: exercise.description, prompt: 'What comes up for you?' }],
  };

  const totalSteps = content.steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(responses);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStart = () => {
    setIsStarted(true);
    setCurrentStep(0);
  };

  const currentStepData = content.steps[currentStep];
  const Icon = exercise.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {!isStarted ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <DialogTitle>{exercise.title}</DialogTitle>
              </div>
              <DialogDescription>{content.intro}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>{exercise.duration}</span>
                <span className="mx-2">•</span>
                <Badge variant="secondary" className="text-xs">
                  {exercise.difficulty}
                </Badge>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-medium">Benefits:</h4>
                <ul className="space-y-1">
                  {exercise.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Sparkles className="w-3 h-3 text-primary" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              {isCompleted && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-accent/10 text-accent">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm">You have completed this exercise before</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={handleStart} className="gap-2">
                <Play className="w-4 h-4" />
                Begin Exercise
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">
                    Step {currentStep + 1} of {totalSteps}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{exercise.title}</span>
                </div>
                <Progress value={progress} className="h-1" />
              </div>
              <DialogTitle className="text-lg mt-4">{currentStepData.title}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {currentStepData.instruction}
              </p>

              {currentStepData.prompt && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">{currentStepData.prompt}</label>
                  <Textarea
                    value={responses[currentStep] || ''}
                    onChange={(e) => setResponses({ ...responses, [currentStep]: e.target.value })}
                    placeholder="Take your time to reflect and respond..."
                    className="min-h-24 resize-none"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 0}
                className="gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <Button onClick={handleNext} className="gap-1">
                {currentStep === totalSteps - 1 ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Complete
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
